import { For, createSignal, type Component } from 'solid-js';

import {
  SETTINGS,
  SLIDERS,
  DEFAULT_SETTINGS,
  browserStorage,
  loadSettings,
  saveSettings,
  type Settings,
} from '../state/settings';

/**
 * Options, from the title screen.
 *
 * ── Why it does not go through the store ────────────────────────────────────
 *
 * The title screen now comes **first**, before the gate and before Babylon exists — so
 * there is no store to ask and no scene to apply anything to. It reads and writes the
 * persisted settings directly, and the game picks them up at boot through the same
 * `loadSettings` call it already made.
 *
 * That is not a second source of truth: `state/settings.ts` is the one model, and both this
 * and the escape menu are views of it. What differs is *when* — this one edits before the
 * session starts, the escape menu edits a session in flight and applies live.
 *
 * The one thing a player reaches for before they have started playing is the music volume,
 * which is why this exists at all rather than making them enter the world first.
 */
export const TitleOptions: Component<{ onClose: () => void }> = (props) => {
  const storage = browserStorage();
  /**
   * The shipped defaults, used only when nothing has been saved yet.
   *
   * The *visual* toggles start on because that is the game's intended look — a first-time
   * player should see it as designed, and turn things off if their machine struggles.
   */
  // The store's own defaults, not a second copy: the title screen and the running game must
  // agree about what a fresh install looks like. No `renderFeatures` here — this panel is
  // shown before a scene exists, so there is no capability to intersect with yet.
  const [settings, setSettings] = createSignal<Settings>(loadSettings(storage, DEFAULT_SETTINGS));

  const update = (patch: Partial<Settings>) => {
    const next = { ...settings(), ...patch };
    setSettings(next);
    // Written on every change rather than on close: a player who alt-tabs away mid-slider
    // should not lose the change, and there is no "apply" button to imply otherwise.
    saveSettings(storage, next);
  };

  return (
    <div class="codex" data-testid="title-options-panel">
      <div class="plate plate--riveted codex__panel hud-interactive">
        <h2 class="codex__title">Options</h2>
        <p class="codex__lede">Saved as you change them, and used from the next session on.</p>
        <hr class="rule" />

        <For each={SLIDERS}>
          {(slider) => (
            <label class="slider" data-testid={`title-slider-${slider.key}`}>
              <span class="slider__label">{slider.label}</span>
              <input
                type="range"
                class="slider__input"
                min="0"
                max="100"
                value={Math.round(settings()[slider.key] * 100)}
                aria-label={slider.label}
                onInput={(e) => update({ [slider.key]: Number(e.currentTarget.value) / 100 })}
              />
              <span class="slider__value">{Math.round(settings()[slider.key] * 100)}</span>
            </label>
          )}
        </For>

        <For each={SETTINGS}>
          {(setting) => (
            <label class="toggle" data-testid={`title-setting-${setting.key}`}>
              <input
                type="checkbox"
                class="toggle__input"
                checked={settings()[setting.key]}
                onChange={(e) => update({ [setting.key]: e.currentTarget.checked })}
              />
              <span class="toggle__box" aria-hidden="true">
                {settings()[setting.key] ? '✓' : ''}
              </span>
              <span class="toggle__text">
                <span class="toggle__label">{setting.label}</span>
                <span class="toggle__hint">{setting.hint}</span>
              </span>
            </label>
          )}
        </For>

        {/*
          **No availability marks here**, unlike the escape menu. Whether an effect *can* be
          built is a fact about a live scene — the escape menu greys a toggle whose
          post-process failed to compile — and there is no scene yet. Showing a guess would
          be worse than showing the plain choice.
        */}
        <button
          type="button"
          class="sigil-button"
          data-testid="title-options-close"
          onClick={() => props.onClose()}
        >
          <span>Back</span>
        </button>
      </div>
    </div>
  );
};
